/*
 * Project: bellcloud
 * File: apps/frontend/src/utils/logDownloadSession.ts
 * Author: SWAN
 * Created: 2025-01-27
 * Description: 로그 다운로드 세션 관리 유틸리티
 */

import type {
  LogDownloadSession,
  LogTypeDownloadInfo,
  RawChunkData,
  LogDownloadSessionStatus,
} from '@packages/types/device/common/log-download-session';
import type { LogDownloadTransportProtocol } from '@/types/connection';
import { v4 as uuidv4 } from 'uuid';

/**
 * 로그 다운로드 세션 관리 클래스
 */
class LogDownloadSessionManager {
  private sessions: Map<string, LogDownloadSession> = new Map();
  private currentSessionId: string | null = null;

  /**
   * 새 세션 생성
   */
  createSession(
    serialNumber: string,
    connectionType: LogDownloadTransportProtocol,
    requestedFileTypes: number[]
  ): string {
    const sessionId = uuidv4();
    const session: LogDownloadSession = {
      session_id: sessionId,
      serial_number: serialNumber,
      connection_type: connectionType,
      requested_file_types: requestedFileTypes,
      log_type_infos: new Map(),
      overall_progress: 0,
      status: 'pending',
      started_at: new Date(),
    };

    this.sessions.set(sessionId, session);
    this.currentSessionId = sessionId;

    return sessionId;
  }

  /**
   * 로그 타입 정보 초기화
   */
  initLogTypeInfo(sessionId: string, fileType: number, logId: string, numPackets: number): void {
    const session = this.sessions.get(sessionId);
    if (!session) {
      throw new Error(`세션을 찾을 수 없습니다: ${sessionId}`);
    }

    const logTypeInfo: LogTypeDownloadInfo = {
      file_type: fileType,
      log_id: logId,
      num_packets: numPackets,
      downloaded_packets: 0,
      progress: 0,
      status: 'downloading',
      chunks: [],
    };

    session.log_type_infos.set(fileType, logTypeInfo);
    session.status = 'downloading';
  }

  /**
   * 청크 추가 (payload chunk_data만 저장)
   */
  addChunk(sessionId: string, fileType: number, chunkIndex: number, chunkData: Uint8Array): void {
    const session = this.sessions.get(sessionId);
    if (!session) {
      throw new Error(`세션을 찾을 수 없습니다: ${sessionId}`);
    }

    const logTypeInfo = session.log_type_infos.get(fileType);
    if (!logTypeInfo) {
      throw new Error(`로그 타입 정보를 찾을 수 없습니다: ${fileType}`);
    }

    // 중복 청크 체크
    const existingChunk = logTypeInfo.chunks.find(c => c.chunk_index === chunkIndex);
    if (existingChunk) {
      console.warn(`⚠️ [SessionManager] 중복 청크 무시: ${chunkIndex}`);
      return;
    }

    // 원본 데이터를 number[]로 변환하여 저장
    const rawChunk: RawChunkData = {
      chunk_index: chunkIndex,
      chunk_data: Array.from(chunkData),
      received_at: new Date(),
    };

    logTypeInfo.chunks.push(rawChunk);
    logTypeInfo.downloaded_packets = logTypeInfo.chunks.length;
    logTypeInfo.progress = (logTypeInfo.downloaded_packets / logTypeInfo.num_packets) * 100;

    // 전체 진행률 업데이트
    this.updateOverallProgress(sessionId);
  }

  /**
   * 전체 진행률 업데이트
   */
  private updateOverallProgress(sessionId: string): void {
    const session = this.sessions.get(sessionId);
    if (!session) return;

    let totalProgress = 0;
    let count = 0;

    session.log_type_infos.forEach(info => {
      totalProgress += info.progress;
      count++;
    });

    session.overall_progress = count > 0 ? totalProgress / count : 0;
  }

  /**
   * 로그 타입 완료 처리
   */
  completeLogType(sessionId: string, fileType: number): void {
    const session = this.sessions.get(sessionId);
    if (!session) {
      throw new Error(`세션을 찾을 수 없습니다: ${sessionId}`);
    }

    const logTypeInfo = session.log_type_infos.get(fileType);
    if (!logTypeInfo) {
      throw new Error(`로그 타입 정보를 찾을 수 없습니다: ${fileType}`);
    }

    logTypeInfo.status = 'completed';
    logTypeInfo.progress = 100;

    // 모든 로그 타입이 완료되었는지 확인
    const allCompleted = Array.from(session.log_type_infos.values()).every(
      info => info.status === 'completed'
    );

    if (allCompleted) {
      session.status = 'completed';
      session.completed_at = new Date();
    }
  }

  /**
   * 로그 타입 실패 처리
   */
  failLogType(sessionId: string, fileType: number, errorMessage: string): void {
    const session = this.sessions.get(sessionId);
    if (!session) return;

    const logTypeInfo = session.log_type_infos.get(fileType);
    if (!logTypeInfo) return;

    logTypeInfo.status = 'failed';
    logTypeInfo.error_message = errorMessage;

    console.error('❌ [SessionManager] 로그 타입 실패:', {
      sessionId,
      fileType,
      error: errorMessage,
    });
  }

  /**
   * 세션 실패 처리
   */
  failSession(sessionId: string, errorMessage: string): void {
    const session = this.sessions.get(sessionId);
    if (!session) return;

    session.status = 'failed';
    session.error_message = errorMessage;
    session.completed_at = new Date();

    console.error('❌ [SessionManager] 세션 실패:', {
      sessionId,
      error: errorMessage,
    });
  }

  /**
   * 세션 조회
   */
  getSession(sessionId: string): LogDownloadSession | undefined {
    return this.sessions.get(sessionId);
  }

  /**
   * 현재 세션 조회
   */
  getCurrentSession(): LogDownloadSession | undefined {
    if (!this.currentSessionId) return undefined;
    return this.sessions.get(this.currentSessionId);
  }

  /**
   * 로그 타입 정보 조회
   */
  getLogTypeInfo(sessionId: string, fileType: number): LogTypeDownloadInfo | undefined {
    const session = this.sessions.get(sessionId);
    if (!session) return undefined;
    return session.log_type_infos.get(fileType);
  }

  /**
   * 청크 데이터 가져오기 (배치 전송용)
   */
  getChunks(sessionId: string, fileType: number): RawChunkData[] {
    const logTypeInfo = this.getLogTypeInfo(sessionId, fileType);
    if (!logTypeInfo) return [];
    return logTypeInfo.chunks;
  }

  /**
   * 청크 데이터 클리어 (메모리 절약)
   */
  clearChunks(sessionId: string, fileType: number): void {
    const logTypeInfo = this.getLogTypeInfo(sessionId, fileType);
    if (!logTypeInfo) return;

    logTypeInfo.chunks = [];
  }

  /**
   * 세션 삭제
   */
  deleteSession(sessionId: string): void {
    this.sessions.delete(sessionId);
    if (this.currentSessionId === sessionId) {
      this.currentSessionId = null;
    }
  }

  /**
   * 모든 세션 클리어
   */
  clearAll(): void {
    this.sessions.clear();
    this.currentSessionId = null;
  }

  /**
   * 누락된 청크 인덱스 조회
   */
  getMissingChunkIndices(sessionId: string, fileType: number): number[] {
    const logTypeInfo = this.getLogTypeInfo(sessionId, fileType);
    if (!logTypeInfo) return [];

    const receivedIndices = new Set(logTypeInfo.chunks.map(c => c.chunk_index));
    const missing: number[] = [];

    for (let i = 0; i < logTypeInfo.num_packets; i++) {
      if (!receivedIndices.has(i)) {
        missing.push(i);
      }
    }

    return missing;
  }

  /**
   * 진행 상태 요약
   */
  getProgressSummary(sessionId: string): {
    overall: number;
    byType: Array<{
      fileType: number;
      progress: number;
      downloaded: number;
      total: number;
      status: LogDownloadSessionStatus;
    }>;
  } {
    const session = this.sessions.get(sessionId);
    if (!session) {
      return { overall: 0, byType: [] };
    }

    const byType = Array.from(session.log_type_infos.values()).map(info => ({
      fileType: info.file_type,
      progress: info.progress,
      downloaded: info.downloaded_packets,
      total: info.num_packets,
      status: info.status,
    }));

    return {
      overall: session.overall_progress,
      byType,
    };
  }
}

// 싱글톤 인스턴스
export const logDownloadSessionManager = new LogDownloadSessionManager();
